import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { useApi } from "../context/apiContext";
import DoctorList from "../doctorlist/DoctorList";
import LoadingOverlay from "../../utility/LoadingOverlay";
import { FaCalendarAlt, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Notification from "../../utility/Notification";
import { selectCustomStyle } from "../../utility/selectCustomStyle";
import CheckAvailability from "./CheckAvailability";
import DateRangeInline from "./DateRangeInline";
import { getISTDate } from "../../utility/getISTDate";

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatTimeTo24 = (timeStr) => {
  // Converts "01:00:00 PM" → "13:00"
  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":");
  hours = parseInt(hours, 10);

  if (modifier === "PM" && hours !== 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};

const AppointmentSelect = () => {
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const slotsSectionRef = useRef(null);

  const [timeRange, setTimeRange] = useState({
    from: "", // e.g., "13:00"
    to: "", // e.g., "14:00"
  });

  // Then inside your component:
  const [dateRange, setDateRange] = useState(() => {
    const istToday = getISTDate();
    const istTenDaysLater = new Date(istToday);
    istTenDaysLater.setDate(istToday.getDate() + 10);

    return {
      fromDate: istToday,
      toDate: istTenDaysLater,
    };
  });

  // const [dateRange, setDateRange] = useState({
  //   fromDate: new Date(),
  //   toDate: new Date(new Date().setDate(new Date().getDate() + 10)),
  // });

  const updateUrlParams = (doctorId, specialityId, fromDate, toDate) => {
    const params = new URLSearchParams();
    if (doctorId) params.set("doctor", doctorId);
    if (specialityId) params.set("speciality", specialityId);
    if (fromDate) params.set("from", fromDate);
    if (toDate) params.set("to", toDate);

    navigate(`?${params.toString()}`, { replace: true });
  };

  const {
    specialityList,
    fetchTokenAndSpecialityList,
    doctorList,
    setSelectedSpecialty,
    fetchTokenAndSpecialityDoctorList,
    selectedSpecialty,
    setSelectedDoctor,
    selectedDoctor,
    loadingDoctor,
    addNotification,
    removeNotification,
    notifications,
    fetchDoctorSlots,
    isLoadingSpecialties,
  } = useApi();

  useEffect(() => {
    fetchTokenAndSpecialityList();
  }, []);

  useEffect(() => {
    if (selectedSpecialty?.value) {
      const fetchData = async () => {
        try {
          const data =
            await fetchTokenAndSpecialityDoctorList(selectedSpecialty);
          if (data?.length === 0) {
            navigate("/book-an-appointment", {
              state: {
                doctorName: selectedDoctor?.label,
              },
            });
          }
        } catch (error) {
          console.error("Error fetching doctors:", error);
        }
      };

      fetchData();
    }
  }, [selectedSpecialty?.value]);

  const specialties =
    specialityList?.map((item) => ({
      value: item.KEY_ID,
      label: item.KEY_VALUE,
    })) || [];

  const doctors =
    doctorList?.map((item) => ({
      value: item.KEY_ID || item?.key_id,
      label: item.KEY_VALUE || item?.key_value,
      image: item.DOCTOR_IMAGE || "",
      about: item.ABOUTDOC || "",
      doNotShowLeappt: String(item.DONTSHOWONLINESLOTS) === "True",
    })) || [];

  const handleSpecialtyChange = (selectedOption) => {
    setSelectedSpecialty(selectedOption);
    setSelectedDoctor(null);
    // if (selectedOption) {
    //   updateUrlParams(
    //     null,
    //     selectedOption.value || null,
    //     formatDate(dateRange.fromDate),
    //     formatDate(dateRange.toDate),
    //   );
    // }
    setSlots([]);
    setHasSearched(false);
  };

  const handleDoctorChange = (selectedOption) => {
    setSelectedDoctor(selectedOption);

    // if (selectedOption) {
    //   updateUrlParams(
    //     selectedOption.value,
    //     selectedSpecialty.value,
    //     formatDate(dateRange.fromDate),
    //     formatDate(dateRange.toDate),
    //   );
    // }
    setSlots([]);
    setHasSearched(false);
  };

  // 2. Modified handleBookAppointment
  const handleBookAppointment = async () => {
    if (!selectedDoctor && !selectedSpecialty) {
      addNotification("Please select both a Specialty and Doctor to proceed");
      return;
    }

    if (!selectedDoctor) {
      addNotification("Please select a Doctor");
      return;
    }

    setLoading(true);
    try {
      // updateUrlParams(
      //   selectedDoctor.value,
      //   selectedSpecialty.value,
      //   formatDate(dateRange.fromDate),
      //   formatDate(dateRange.toDate),
      // );

      // Call the API function
      const slotsData = await fetchDoctorSlots(
        selectedDoctor.value,
        formatDate(dateRange.fromDate),
        formatDate(dateRange.toDate),
      );

      if (slotsData.length > 0) {
        setSlots(slotsData);
        setHasSearched(true);
        setTimeout(() => {
          slotsSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      } else {
        navigate("/book-an-appointment", {
          state: {
            doctorName: selectedDoctor?.label,
          },
        });
      }
    } catch (error) {
      addNotification("Failed to fetch available slots");
    } finally {
      setLoading(false);
    }
  };
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const newDate = new Date(value);

    setDateRange((prev) => {
      const newRange = {
        ...prev,
        [name]: newDate,
      };

      // Validation: fromDate should not be after toDate
      if (name === "fromDate" && newDate > prev.toDate) {
        addNotification("From Date cannot be after To Date.");
        return prev; // Do not update
      }

      if (name === "toDate" && newDate < prev.fromDate) {
        addNotification("To Date cannot be before From Date.");
        return prev; // Do not update
      }

      // if (selectedDoctor) {
      //   updateUrlParams(
      //     selectedDoctor.value,
      //     selectedSpecialty.value,
      //     formatDate(newRange.fromDate),
      //     formatDate(newRange.toDate),
      //   );
      // }

      return newRange;
    });
  };

  // useEffect(() => {
  //   const loadInitialData = async () => {
  //     const params = new URLSearchParams(window.location.search);
  //     const doctorId = params.get("doctor");
  //     const specialityId = params.get("speciality");

  //     let fromDate = params.get("from");
  //     let toDate = params.get("to");

  //     if (!fromDate || !toDate) {
  //       const today = getISTDate(); // ✅
  //       const tenDaysLater = new Date(getISTDate());
  //       tenDaysLater.setDate(tenDaysLater.getDate() + 10); // ✅

  //       fromDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  //       toDate = `${tenDaysLater.getFullYear()}-${String(tenDaysLater.getMonth() + 1).padStart(2, "0")}-${String(tenDaysLater.getDate()).padStart(2, "0")}`;
  //     }

  //     if (specialityId && specialityList) {
  //       const specialty = specialityList.find(
  //         (d) => String(d.KEY_ID) === String(specialityId),
  //       );

  //       if (specialty) {
  //         setSelectedSpecialty({
  //           value: specialty.KEY_ID,
  //           label: specialty.KEY_VALUE,
  //         });
  //       } else {
  //         // Handle case when specialty not found
  //         console.warn(`Specialty with ID ${specialityId} not found`);
  //       }
  //     }

  //     if (doctorId && doctorList) {
  //       const doctor = doctorList.find(
  //         (d) => String(d.KEY_ID) === String(doctorId),
  //       );
  //       if (doctor) {
  //         setSelectedDoctor({
  //           value: doctor?.KEY_ID,
  //           label: doctor?.KEY_VALUE,
  //           image: doctor?.DOCTOR_IMAGE,
  //           about: doctor?.ABOUTDOC,
  //           doNotShowLeappt: String(doctor.DONTSHOWONLINESLOTS) === "True",
  //         });
  //         setHasSearched(true);
  //       } else {
  //         // If doctor not found in current list, try fetching the list
  //         // You might need to implement fetchTokenAndSpecialityDoctorList here
  //       }
  //     }

  //     if (fromDate && toDate) {
  //       // ✅ Parse date parts manually to avoid UTC conversion issue
  //       const [fy, fm, fd] = fromDate.split("-").map(Number);
  //       const [ty, tm, td] = toDate.split("-").map(Number);

  //       const parsedFromDate = new Date(fy, fm - 1, fd, 0, 0, 0, 0); // local midnight
  //       const parsedToDate = new Date(ty, tm - 1, td, 0, 0, 0, 0); // local midnight

  //       if (
  //         !isNaN(parsedFromDate.getTime()) &&
  //         !isNaN(parsedToDate.getTime())
  //       ) {
  //         setDateRange({
  //           fromDate: parsedFromDate,
  //           toDate: parsedToDate,
  //         });
  //       }
  //     }

  //     if (doctorId && fromDate && toDate) {
  //       setLoading(true);
  //       try {
  //         const slotsData = await fetchDoctorSlots(doctorId, fromDate, toDate);
  //         if (slotsData.length > 0) {
  //           setSlots(slotsData);
  //           setHasSearched(true);
  //           setTimeout(() => {
  //             slotsSectionRef.current?.scrollIntoView({
  //               behavior: "smooth",
  //               block: "start",
  //             });
  //           }, 100);
  //         } else {
  //           navigate("/book-an-appointment", {
  //             state: {
  //               doctorName: selectedDoctor?.label,
  //             },
  //           });
  //         }
  //         // setSlots(slotsData);

  //         // setHasSearched(true);
  //       } catch (error) {
  //         console.error("Failed to load initial slots:", error);
  //       } finally {
  //         setLoading(false);
  //       }
  //     }
  //   };

  //   loadInitialData();
  // }, [doctorList, specialityList]);

  const formatTime = (timeString) => {
    if (!timeString) return "";

    return timeString.replace(/:00/g, "").replace(/\s+/g, " ");
  };

  const slotsByDate = slots.reduce((acc, slot) => {
    const slotStart24 = formatTimeTo24(slot.SLOT_START);

    const isWithinTimeRange =
      !timeRange.from ||
      !timeRange.to ||
      (timeRange.from <= slotStart24 && slotStart24 <= timeRange.to);

    if (isWithinTimeRange) {
      if (!acc[slot.SLOT_DATE]) {
        acc[slot.SLOT_DATE] = [];
      }

      acc[slot.SLOT_DATE].push({
        start: formatTime(slot.SLOT_START),
        end: formatTime(slot.SLOT_END),
      });
    }

    return acc;
  }, {});

  const handleFormCondition = () => {
    // if (!selectedDoctor) return;

    if (!selectedDoctor && !selectedSpecialty) {
      addNotification("Please select both a Specialty and Doctor to proceed");
      return;
    }

    if (!selectedDoctor) {
      addNotification("Please select a Doctor");
      return;
    }

    if (selectedDoctor.doNotShowLeappt) {
      navigate("/book-an-appointment", {
        state: {
          doctorName: selectedDoctor?.label,
        },
      });
    } else {
      handleBookAppointment();
    }
  };

  return (
    <>
      <LoadingOverlay isLoading={loading || loadingDoctor} />
      {/* <LoadingOverlay isLoading={true} /> */}
      <section id="home-search-section">
        <div className="" ref={slotsSectionRef}>
          <h2>Schedule Your Appointment Online</h2>
          {/* <p>
            Get <span>10% discount</span> on all appointments* booked and paid
            online.
          </p> */}

          <div className="home-search-section mt-5">
            <div className="search-box">
              <div className="doctor-search">
                <div className="ding">
                  <Select
                    options={specialties}
                    placeholder={
                      isLoadingSpecialties
                        ? "Loading specialties..."
                        : "Select Specialty"
                    }
                    className="react-select"
                    onChange={handleSpecialtyChange}
                    value={selectedSpecialty}
                    styles={selectCustomStyle}
                    menuPortalTarget={document.body}
                    isClearable={true}
                    isLoading={isLoadingSpecialties} // 👈 shows spinner inside the select
                    isDisabled={isLoadingSpecialties} // 👈 prevents interaction
                    loadingMessage={() => "Fetching specialties..."}
                  />
                </div>
              </div>

              <div className="location-search">
                <div className="ding">
                  <Select
                    options={doctors}
                    placeholder="Search by Doctor's name"
                    className="react-select"
                    onChange={handleDoctorChange}
                    value={selectedDoctor}
                    styles={selectCustomStyle}
                    menuPortalTarget={document.body}
                    isClearable={true}
                    noOptionsMessage={() =>
                      selectedSpecialty
                        ? "No doctors found"
                        : "Please Select Specialty"
                    }
                  />
                </div>
              </div>

              <DateRangeInline
                fromDate={dateRange.fromDate}
                toDate={dateRange.toDate}
                onChange={(from, to) => {
                  setDateRange({ fromDate: from, toDate: to });
                  // if (selectedDoctor) {
                  //   updateUrlParams(
                  //     selectedDoctor.value,
                  //     selectedSpecialty.value,
                  //     formatDate(from),
                  //     formatDate(to),
                  //   );
                  // }
                }}
              />
              {/* <button className="search-now" onClick={handleBookAppointment}>
                Book an Appointment
              </button> */}
            </div>
          </div>
          <div className="appointment-btn-wrapper">
            <button
              // onClick={handleBookAppointment}
              onClick={handleFormCondition}
              className="appointment-btn"
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#0077b6")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#0095da")
              }
            >
              <FaSearch style={{ fontSize: "13px" }} />
              Book an Appointment
            </button>
          </div>
        </div>
      </section>

      {hasSearched ? (
        slots.length > 0 ? (
          selectedDoctor?.doNotShowLeappt ? null : (
            <section
              className="doc-section"
              // style={{ marginTop: "40px" }}
              // style={{ marginTop: "-165px", zIndex: "1" }}
              // ref={slotsSectionRef}
            >
              <div className="midbox-wiki">
                <CheckAvailability
                  fromDate={dateRange.fromDate}
                  toDate={dateRange.toDate}
                  onDateChange={(from, to) => {
                    setDateRange({ fromDate: from, toDate: to });
                    // if (selectedDoctor) {
                    //   updateUrlParams(
                    //     selectedDoctor.value,
                    //     selectedSpecialty.value,
                    //     formatDate(from),
                    //     formatDate(to),
                    //   );
                    // }
                  }}
                  onCheck={handleBookAppointment}
                />
                {/* <div className="date-selection-card">
                <h3>Check Availability</h3>
                <div className="date-range-picker">
                  <div className="date-input">
                    <label htmlFor="fromDate">
                      <FaCalendarAlt className="calendar-icon" />
                      From Date
                    </label>
                    <input
                      type="date"
                      id="fromDate"
                      name="fromDate"
                      value={dateRange.fromDate.toISOString().split("T")[0]}
                      onChange={handleDateChange}
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>

                  <div className="date-input">
                    <label htmlFor="toDate">
                      <FaCalendarAlt className="calendar-icon" />
                      To Date
                    </label>
                    <input
                      type="date"
                      id="toDate"
                      name="toDate"
                      value={dateRange.toDate.toISOString().split("T")[0]}
                      onChange={handleDateChange}
                      min={dateRange.fromDate.toISOString().split("T")[0]}
                    />
                  </div>

                  <button
                    className="check-availability-btn"
                    onClick={handleBookAppointment}
                  >
                    <FaSearch className="search-icon" />
                    Check Availability
                  </button>
                </div>
              </div> */}

                <DoctorList
                  slots={slots}
                  selectedDoctor={selectedDoctor}
                  selectedSpecialty={selectedSpecialty}
                  addNotification={addNotification}
                  fromDate={dateRange.fromDate}
                  toDate={dateRange.toDate}
                  slotsByDate={slotsByDate}
                  doctorList={doctorList}
                />
              </div>
            </section>
          )
        ) : (
          <div className="flex flex-col items-center justify-center mt-10 text-gray-500 bg-gray-50 p-6 rounded-lg shadow-sm border border-gray-200">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 mb-3 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 0h6"
              />
            </svg>
            <p className="text-lg font-medium">No slots available</p>
          </div>
        )
      ) : null}
      {notifications.map((notification) => (
        <Notification
          key={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
      <style jsx>{`
        .appointment-container {
          position: relative;
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .date-selection-card {
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          padding: 20px;
          margin-bottom: 30px;
        }

        .date-selection-card h3 {
          margin-top: 0;
          color: #2b7a78;
          font-size: 1.2rem;
        }

        .date-range-picker {
          display: flex;
          gap: 15px;
          align-items: flex-end;
          flex-wrap: wrap;
        }

        .date-input {
          flex: 1;
          min-width: 200px;
        }

        .date-input label {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          font-weight: 500;
          color: #555;
        }

        .calendar-icon {
          margin-right: 8px;
          color: #2b7a78;
        }

        .date-input input {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
        }

        .check-availability-btn {
          background: #2b7a78;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.3s;
        }

        .check-availability-btn:hover {
          background: #1f5e5b;
        }

        .search-icon {
          font-size: 0.9rem;
        }
      `}</style>
    </>
  );
};

export default AppointmentSelect;
